/**
 * Photo byte inspection, sanitization and INDEPENDENT VERIFICATION (M5 / S05-02).
 *
 * Pure `Uint8Array` work — no DOM, no network, no canvas — so every rule below
 * is testable directly rather than through a browser.
 *
 * THE SAFETY PROPERTY, PROVEN THE HARD WAY IN RISK PROBE C:
 *
 *     sanitize → INDEPENDENTLY VERIFY → only verified-clean bytes are eligible
 *
 * A careful, reasonable stripper — the one anybody writes first — cleaned an
 * ordinary JPEG perfectly and SILENTLY LEAKED GPS on two shapes real Android
 * cameras produce: a Motion Photo (a whole video appended after the image) and
 * an MPF multi-image (a SECOND complete photo, with its own EXIF and GPS,
 * appended after the first). It reported success both times. So the property is
 * NOT "the stripper is correct" — it is that a separate reader re-examines the
 * OUTPUT bytes and refuses anything it cannot prove clean.
 *
 * DETECTION VERSUS REMOVAL — a deliberate asymmetry. The EXIF reader here is a
 * minimal TIFF walker used ONLY to REPORT what a source contained. Removal
 * never depends on it: whole APP segments and whole PNG ancillary chunks are
 * dropped wholesale. A detection bug can make a report less informative; it can
 * never make the output less safe. Where the reader cannot parse something it
 * says `unknown`, never `absent`.
 */

export type PhotoFormat = 'jpeg' | 'png' | 'heic' | 'webp' | 'gif' | 'unknown';

/** PRESENT / ABSENT / UNKNOWN — "could not tell" is never reported as absent. */
export type Tri = 'present' | 'absent' | 'unknown';

/** A metadata container found in the source, identified precisely. */
export interface MetadataSegment {
  /** APPn marker number, or the PNG chunk type. */
  readonly label: string;
  /**
   * What it actually is, from the payload signature — never from the marker
   * number alone. APP2 carries BOTH benign ICC colour profiles and MPF
   * multi-image indexes, and treating them alike is the mistake that leaks a
   * hidden second photo.
   */
  readonly kind:
    | 'jfif'
    | 'exif'
    | 'xmp'
    | 'icc'
    | 'mpf'
    | 'iptc'
    | 'adobe'
    | 'comment'
    | 'png-text'
    | 'png-exif'
    | 'unknown';
  readonly byteSize: number;
}

export interface PhotoInspection {
  readonly format: PhotoFormat;
  readonly byteSize: number;
  /** Dimensions from the file's own structure (SOFn / IHDR), not from a decoder. */
  readonly width: number | null;
  readonly height: number | null;
  readonly exif: Tri;
  readonly gps: Tri;
  readonly xmp: Tri;
  readonly iptc: Tri;
  /** An MPF index means a second image may be appended. Always treated as unsafe. */
  readonly mpf: Tri;
  /** APP0/JFIF and ICC are benign structure — reported so they are not confused with the above. */
  readonly jfif: Tri;
  readonly icc: Tri;
  /** EXIF Orientation 1–8, needed because sanitization removes the tag (I-040). */
  readonly orientation: number | null;
  /** EXIF DateTimeOriginal / DateTime, verbatim, unparsed. */
  readonly capturedAtRaw: string | null;
  /** EXIF OffsetTimeOriginal / OffsetTime, verbatim (`+05:30`). */
  readonly utcOffsetRaw: string | null;
  readonly segments: readonly MetadataSegment[];
  /** Bytes after the true end of image — the Motion Photo / MPF signature. */
  readonly trailingBytes: number;
  /** Why the structure could not be walked, if it could not. */
  readonly error: string | null;
}

const u8 = (bytes: Uint8Array, at: number): number => bytes[at] ?? 0;

const ascii = (bytes: Uint8Array, at: number, length: number): string =>
  String.fromCharCode(...bytes.subarray(at, at + length));

const startsWith = (bytes: Uint8Array, sig: readonly number[]): boolean =>
  sig.every((b, i) => u8(bytes, i) === b);

/**
 * The format, from the BYTES — never from the filename or `File.type`.
 * A picker and a WebView are both free to report whatever they like; the first
 * bytes are the only thing a decoder will believe.
 */
export function sniffFormat(bytes: Uint8Array): PhotoFormat {
  if (bytes.length >= 3 && startsWith(bytes, [0xff, 0xd8, 0xff])) return 'jpeg';
  if (bytes.length >= 8 && startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return 'png';
  }
  if (bytes.length >= 12 && ascii(bytes, 4, 4) === 'ftyp') {
    const brand = ascii(bytes, 8, 4);
    if (['heic', 'heix', 'hevc', 'heim', 'heis', 'hevm', 'mif1', 'msf1'].includes(brand)) {
      return 'heic';
    }
  }
  if (bytes.length >= 12 && ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 4) === 'WEBP') {
    return 'webp';
  }
  if (bytes.length >= 6 && ascii(bytes, 0, 3) === 'GIF') return 'gif';
  return 'unknown';
}

/* ==========================================================================
 * JPEG
 * ========================================================================== */

/** APPn (0xE0–0xEF) and COM (0xFE) are the metadata carriers. Everything else is picture. */
const isJpegMetadataMarker = (marker: number): boolean =>
  (marker >= 0xe0 && marker <= 0xef) || marker === 0xfe;

/**
 * Walk the entropy-coded scan and return the index of the next REAL marker.
 *
 * This is the function whose absence leaked GPS in Probe C. Inside compressed
 * scan data a 0xFF byte is not a marker: it is either stuffed (`FF 00`) or a
 * restart marker (`FF D0`–`FF D7`). Anything else genuinely ends the scan,
 * including `FF D9`, the end of the image.
 */
function endOfScan(bytes: Uint8Array, start: number): number {
  let i = start;
  while (i < bytes.length) {
    if (u8(bytes, i) !== 0xff) {
      i++;
      continue;
    }
    const next = u8(bytes, i + 1);
    if (next === 0x00 || next === 0xff || (next >= 0xd0 && next <= 0xd7)) {
      i += 2;
      continue;
    }
    return i;
  }
  return bytes.length;
}

interface JpegSegment {
  marker: number;
  at: number;
  length: number;
}

interface JpegWalk {
  segments: JpegSegment[];
  /** Index one past the EOI marker, or -1 if there is none. */
  endOfImage: number;
  width: number | null;
  height: number | null;
  error: string | null;
}

function walkJpeg(bytes: Uint8Array): JpegWalk {
  const segments: JpegSegment[] = [];
  let width: number | null = null;
  let height: number | null = null;

  if (bytes.length < 4 || u8(bytes, 0) !== 0xff || u8(bytes, 1) !== 0xd8) {
    return { segments, endOfImage: -1, width, height, error: 'not a JPEG' };
  }

  let i = 2;
  while (i < bytes.length) {
    if (u8(bytes, i) !== 0xff) {
      return { segments, endOfImage: -1, width, height, error: `desync at byte ${String(i)}` };
    }
    let marker = u8(bytes, i + 1);
    while (marker === 0xff) {
      i++;
      marker = u8(bytes, i + 1);
    }
    if (marker === 0xd9) return { segments, endOfImage: i + 2, width, height, error: null };

    if (marker === 0xda) {
      if (i + 4 > bytes.length) {
        return { segments, endOfImage: -1, width, height, error: 'truncated SOS header' };
      }
      const headerLength = (u8(bytes, i + 2) << 8) | u8(bytes, i + 3);
      const next = endOfScan(bytes, i + 2 + headerLength);
      if (next >= bytes.length) {
        return { segments, endOfImage: -1, width, height, error: 'scan ran off the end' };
      }
      i = next;
      continue;
    }

    if (i + 4 > bytes.length) {
      return { segments, endOfImage: -1, width, height, error: 'truncated segment header' };
    }
    const length = (u8(bytes, i + 2) << 8) | u8(bytes, i + 3);
    if (length < 2 || i + 2 + length > bytes.length) {
      return { segments, endOfImage: -1, width, height, error: 'bad or truncated segment length' };
    }
    // SOF0–SOF15, excluding DHT/JPG/DAC which share the range.
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      height = (u8(bytes, i + 5) << 8) | u8(bytes, i + 6);
      width = (u8(bytes, i + 7) << 8) | u8(bytes, i + 8);
    }
    segments.push({ marker, at: i, length });
    i += 2 + length;
  }
  return { segments, endOfImage: -1, width, height, error: 'no end-of-image marker' };
}

/**
 * Identify a metadata segment from its PAYLOAD, not its marker number.
 *
 * APP2 is the one that matters: it carries benign ICC colour profiles AND MPF
 * multi-image indexes. An MPF index is how a second complete photo — with its
 * own EXIF and its own GPS — gets attached. They must never be conflated.
 */
function identifyJpegSegment(marker: number, payload: Uint8Array): MetadataSegment['kind'] {
  // Long enough for the longest signature we match — the XMP namespace URI is
  // 28 characters, and a window shorter than the string being searched for can
  // never match it. A 20-byte window silently classified every XMP block as
  // 'unknown' until a test caught it.
  const head = ascii(payload, 0, Math.min(payload.length, 40));
  if (marker === 0xfe) return 'comment';
  if (head.startsWith('JFIF\0') || head.startsWith('JFXX\0')) return 'jfif';
  if (head.startsWith('Exif\0\0')) return 'exif';
  if (head.startsWith('http://ns.adobe.com/xap') || head.startsWith('http://ns.adobe.com/xmp')) {
    return 'xmp';
  }
  if (head.startsWith('ICC_PROFILE\0')) return 'icc';
  if (head.startsWith('MPF\0')) return 'mpf';
  if (head.startsWith('Photoshop 3.0\0')) return 'iptc';
  if (head.startsWith('Adobe')) return 'adobe';
  return 'unknown';
}

/* ---- the orientation domain --------------------------------------------- */

/**
 * THE ONLY DEFINITION OF A VALID ORIENTATION IN THE CLIENT.
 *
 * EXIF defines exactly eight orientations, 1–8. Migration 0006 says the same
 * thing in SQL (`source_orientation is null or between 1 and 8`), and until
 * S05-04 **that constraint was the only place the rule existed** — the reader
 * below took whatever 16-bit number sat in the tag and every layer above it
 * copied that number faithfully to the server, where it was refused.
 *
 * **It was refused on real hardware:** Jerry's Infinix, 2026-08-14, portrait
 * camera photo → `capture_photo_orientation_valid` violation, after the
 * display and thumbnail objects had already uploaded.
 *
 * THE CONTRACT, AND WHAT IT DELIBERATELY REFUSES TO DO:
 *
 *   an integer 1–8  → preserved EXACTLY
 *   anything else   → null
 *
 * `null` means **"no reliable orientation metadata"** and nothing more. It is
 * NOT a claim that the photo is upright, and this function will never invent
 * one: 0 does not become 1, a rotation in degrees is never inferred, and a
 * value nobody defined never becomes a value somebody did. Guessing here would
 * silently rotate a real photograph the wrong way with no error anywhere.
 *
 * An invalid value also NEVER refuses the photo. PT §9: "Every device-derived
 * metadata field is optional. Missing metadata must never block a Capture or
 * photo save." A camera writing a junk orientation is a fact about the camera,
 * not a reason to lose Brandon's evidence.
 *
 * Applied at BOTH ends of the path — here as the tag is read, and again
 * immediately before `attach_capture_photo` — because a photo staged by an
 * older build already holds an invalid value on disk, and only the second
 * application can rescue it.
 */
export function normalizeExifOrientation(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 8
    ? value
    : null;
}

/* ---- the minimal TIFF/EXIF reader — REPORTING ONLY ----------------------- */

interface ExifReport {
  gps: Tri;
  orientation: number | null;
  capturedAtRaw: string | null;
  utcOffsetRaw: string | null;
}

const EMPTY_EXIF: ExifReport = {
  gps: 'unknown',
  orientation: null,
  capturedAtRaw: null,
  utcOffsetRaw: null,
};

function readExifPayload(payload: Uint8Array): ExifReport {
  try {
    if (payload.length < 14 || ascii(payload, 0, 6) !== 'Exif\0\0') return EMPTY_EXIF;
    const tiff = payload.subarray(6);
    const order = ascii(tiff, 0, 2);
    if (order !== 'II' && order !== 'MM') return EMPTY_EXIF;
    const view = new DataView(tiff.buffer, tiff.byteOffset, tiff.byteLength);
    const little = order === 'II';
    if (view.getUint16(2, little) !== 0x2a) return EMPTY_EXIF;

    const found: ExifReport = {
      gps: 'absent',
      orientation: null,
      capturedAtRaw: null,
      utcOffsetRaw: null,
    };

    const readAscii = (offset: number, count: number): string | null => {
      if (offset <= 0 || offset + count > tiff.byteLength) return null;
      const raw = ascii(tiff, offset, count);
      const end = raw.indexOf('\0');
      const value = (end >= 0 ? raw.slice(0, end) : raw).trim();
      return value.length > 0 ? value : null;
    };

    const readIfd = (offset: number, depth: number): void => {
      if (depth > 4 || offset <= 0 || offset + 2 > tiff.byteLength) return;
      const count = view.getUint16(offset, little);
      for (let e = 0; e < count; e++) {
        const entry = offset + 2 + e * 12;
        if (entry + 12 > tiff.byteLength) return;
        const tag = view.getUint16(entry, little);
        const type = view.getUint16(entry + 2, little);
        const valueCount = view.getUint32(entry + 4, little);
        const valueOffset = view.getUint32(entry + 8, little);

        if (tag === 0x8825) found.gps = 'present';
        // FIRST VALID ORIENTATION WINS — and "valid" is doing the work.
        //
        // The value is normalized BEFORE the slot is filled, so an invalid tag
        // leaves the slot null and a later legal tag can still be accepted.
        // A plain `??=` over the raw value would have got this exactly wrong:
        // a leading 0 would occupy the slot and block the real value behind
        // it. Conversely, once a legal value is in, a later junk tag cannot
        // overwrite it — which is the defect this replaces, where the LAST tag
        // in the file won regardless of what it said.
        if (tag === 0x0112 && type === 3 && found.orientation === null) {
          found.orientation = normalizeExifOrientation(view.getUint16(entry + 8, little));
        }
        // 0x9003 DateTimeOriginal, 0x0132 DateTime
        if ((tag === 0x9003 || tag === 0x0132) && found.capturedAtRaw === null) {
          found.capturedAtRaw = readAscii(valueOffset, valueCount);
        }
        // 0x9011 OffsetTimeOriginal, 0x9010 OffsetTime
        if ((tag === 0x9011 || tag === 0x9010) && found.utcOffsetRaw === null) {
          found.utcOffsetRaw = readAscii(valueOffset, valueCount);
        }
        if (tag === 0x8769) readIfd(valueOffset, depth + 1); // Exif sub-IFD
      }
    };

    readIfd(view.getUint32(4, little), 0);
    return found;
  } catch {
    // Cannot parse → UNKNOWN. Never "absent": that would be a claim.
    return EMPTY_EXIF;
  }
}

function inspectJpeg(bytes: Uint8Array): PhotoInspection {
  const walk = walkJpeg(bytes);
  const segments: MetadataSegment[] = [];
  let exif: Tri = 'absent';
  let gps: Tri = 'absent';
  let xmp: Tri = 'absent';
  let iptc: Tri = 'absent';
  let mpf: Tri = 'absent';
  let jfif: Tri = 'absent';
  let icc: Tri = 'absent';
  let orientation: number | null = null;
  let capturedAtRaw: string | null = null;
  let utcOffsetRaw: string | null = null;

  for (const segment of walk.segments) {
    if (!isJpegMetadataMarker(segment.marker)) continue;
    const payload = bytes.subarray(segment.at + 4, segment.at + 2 + segment.length);
    const kind = identifyJpegSegment(segment.marker, payload);
    segments.push({
      label: segment.marker === 0xfe ? 'COM' : `APP${String(segment.marker - 0xe0)}`,
      kind,
      byteSize: segment.length,
    });

    if (kind === 'exif') {
      exif = 'present';
      const read = readExifPayload(payload);
      if (read.gps === 'present') gps = 'present';
      else if (read.gps === 'unknown' && gps === 'absent') gps = 'unknown';
      orientation ??= read.orientation;
      capturedAtRaw ??= read.capturedAtRaw;
      utcOffsetRaw ??= read.utcOffsetRaw;
    }
    if (kind === 'xmp') {
      xmp = 'present';
      // XMP can carry its own GPS. It is detected by container and never
      // parsed — and it is removed either way, so parsing would buy nothing.
      if (gps === 'absent') gps = 'unknown';
    }
    if (kind === 'iptc') iptc = 'present';
    if (kind === 'mpf') mpf = 'present';
    if (kind === 'jfif') jfif = 'present';
    if (kind === 'icc') icc = 'present';
  }

  return {
    format: 'jpeg',
    byteSize: bytes.length,
    width: walk.width,
    height: walk.height,
    exif,
    gps,
    xmp,
    iptc,
    mpf,
    jfif,
    icc,
    orientation,
    capturedAtRaw,
    utcOffsetRaw,
    segments,
    trailingBytes: walk.endOfImage < 0 ? 0 : bytes.length - walk.endOfImage,
    error: walk.error,
  };
}

/**
 * Strip every metadata segment, keep the picture, and STOP AT THE TRUE END OF
 * THE IMAGE — discarding anything appended after it.
 *
 * That last clause is the one that matters: it removes a Motion Photo's
 * trailing video and an MPF file's second embedded photo, both of which carry
 * their own GPS. The entropy-coded scan is copied byte for byte, so the picture
 * is not re-compressed (PT §9: full-resolution source detail is retained).
 */
export function sanitizeJpeg(bytes: Uint8Array): Uint8Array {
  const walk = walkJpeg(bytes);
  if (walk.error !== null) throw new Error(walk.error);

  const parts: Uint8Array[] = [bytes.subarray(0, 2)];
  let i = 2;
  while (i < walk.endOfImage - 2) {
    let marker = u8(bytes, i + 1);
    while (marker === 0xff) {
      i++;
      marker = u8(bytes, i + 1);
    }
    if (marker === 0xd9) break;
    if (marker === 0xda) {
      const headerLength = (u8(bytes, i + 2) << 8) | u8(bytes, i + 3);
      const next = endOfScan(bytes, i + 2 + headerLength);
      parts.push(bytes.subarray(i, next));
      i = next;
      continue;
    }
    const length = (u8(bytes, i + 2) << 8) | u8(bytes, i + 3);
    if (!isJpegMetadataMarker(marker)) parts.push(bytes.subarray(i, i + 2 + length));
    i += 2 + length;
  }
  parts.push(new Uint8Array([0xff, 0xd9]));
  return concat(parts);
}

/**
 * Problems with the OUTPUT. Empty means provably clean.
 *
 * This re-walks bytes it did not produce. It does not know, and must not know,
 * how they were made.
 */
export function verifyJpegClean(bytes: Uint8Array): string[] {
  const walk = walkJpeg(bytes);
  if (walk.error !== null) return [walk.error];

  const problems: string[] = [];
  for (const segment of walk.segments) {
    if (isJpegMetadataMarker(segment.marker)) {
      const payload = bytes.subarray(segment.at + 4, segment.at + 2 + segment.length);
      const kind = identifyJpegSegment(segment.marker, payload);
      problems.push(`metadata segment survived: APP${String(segment.marker - 0xe0)} (${kind})`);
    }
  }
  const trailing = bytes.length - walk.endOfImage;
  if (trailing > 0) problems.push(`${String(trailing)} trailing bytes after end of image`);
  return problems;
}

/* ==========================================================================
 * PNG
 * ========================================================================== */

/** Chunks carrying picture or essential rendering data. Everything else goes. */
const PNG_KEEP = new Set(['IHDR', 'PLTE', 'IDAT', 'IEND', 'tRNS', 'gAMA', 'cHRM', 'sRGB']);

interface PngChunk {
  type: string;
  at: number;
  length: number;
}

function walkPng(bytes: Uint8Array): {
  chunks: PngChunk[];
  endOfImage: number;
  error: string | null;
} {
  const chunks: PngChunk[] = [];
  if (bytes.length < 8 || !startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return { chunks, endOfImage: -1, error: 'not a PNG' };
  }
  let i = 8;
  while (i + 8 <= bytes.length) {
    const view = new DataView(bytes.buffer, bytes.byteOffset + i, 8);
    const length = view.getUint32(0);
    const type = ascii(bytes, i + 4, 4);
    if (i + 12 + length > bytes.length) {
      return { chunks, endOfImage: -1, error: 'truncated PNG chunk' };
    }
    chunks.push({ type, at: i, length });
    i += 12 + length;
    if (type === 'IEND') return { chunks, endOfImage: i, error: null };
  }
  return { chunks, endOfImage: -1, error: 'no IEND chunk' };
}

function inspectPng(bytes: Uint8Array): PhotoInspection {
  const walk = walkPng(bytes);
  const segments: MetadataSegment[] = [];
  let exif: Tri = 'absent';
  let gps: Tri = 'absent';
  let xmp: Tri = 'absent';
  let width: number | null = null;
  let height: number | null = null;
  let orientation: number | null = null;
  let capturedAtRaw: string | null = null;
  let utcOffsetRaw: string | null = null;

  for (const chunk of walk.chunks) {
    if (chunk.type === 'IHDR' && chunk.at + 16 <= bytes.length) {
      const view = new DataView(bytes.buffer, bytes.byteOffset + chunk.at + 8, 8);
      width = view.getUint32(0);
      height = view.getUint32(4);
    }
    if (PNG_KEEP.has(chunk.type)) continue;

    const isText = ['tEXt', 'iTXt', 'zTXt'].includes(chunk.type);
    segments.push({
      label: chunk.type,
      kind: chunk.type === 'eXIf' ? 'png-exif' : isText ? 'png-text' : 'unknown',
      byteSize: chunk.length,
    });

    if (chunk.type === 'eXIf') {
      exif = 'present';
      // PNG eXIf holds raw TIFF without the JPEG APP1 "Exif\0\0" prefix.
      const read = readExifPayload(
        concat([
          Uint8Array.from('Exif\0\0', (c) => c.charCodeAt(0)),
          bytes.subarray(chunk.at + 8, chunk.at + 8 + chunk.length),
        ]),
      );
      if (read.gps === 'present') gps = 'present';
      orientation ??= read.orientation;
      capturedAtRaw ??= read.capturedAtRaw;
      utcOffsetRaw ??= read.utcOffsetRaw;
    }
    // Text chunks routinely carry XMP, which can carry GPS.
    if (isText) {
      xmp = 'unknown';
      if (gps === 'absent') gps = 'unknown';
    }
  }

  return {
    format: 'png',
    byteSize: bytes.length,
    width,
    height,
    exif,
    gps,
    xmp,
    iptc: 'absent',
    mpf: 'absent',
    jfif: 'absent',
    icc: 'absent',
    orientation,
    capturedAtRaw,
    utcOffsetRaw,
    segments,
    trailingBytes: walk.endOfImage < 0 ? 0 : bytes.length - walk.endOfImage,
    error: walk.error,
  };
}

export function sanitizePng(bytes: Uint8Array): Uint8Array {
  const walk = walkPng(bytes);
  if (walk.error !== null) throw new Error(walk.error);
  const parts: Uint8Array[] = [bytes.subarray(0, 8)];
  for (const chunk of walk.chunks) {
    if (PNG_KEEP.has(chunk.type))
      parts.push(bytes.subarray(chunk.at, chunk.at + 12 + chunk.length));
  }
  return concat(parts);
}

export function verifyPngClean(bytes: Uint8Array): string[] {
  const walk = walkPng(bytes);
  if (walk.error !== null) return [walk.error];
  const problems: string[] = [];
  for (const chunk of walk.chunks) {
    if (!PNG_KEEP.has(chunk.type)) problems.push(`chunk survived: ${chunk.type}`);
  }
  const trailing = bytes.length - walk.endOfImage;
  if (trailing > 0) problems.push(`${String(trailing)} trailing bytes after IEND`);
  return problems;
}

/* ==========================================================================
 * Shared
 * ========================================================================== */

export function concat(parts: readonly Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

export function inspectPhoto(bytes: Uint8Array): PhotoInspection {
  const format = sniffFormat(bytes);
  if (format === 'jpeg') return inspectJpeg(bytes);
  if (format === 'png') return inspectPng(bytes);
  return {
    format,
    byteSize: bytes.length,
    width: null,
    height: null,
    exif: 'unknown',
    gps: 'unknown',
    xmp: 'unknown',
    iptc: 'unknown',
    mpf: 'unknown',
    jfif: 'unknown',
    icc: 'unknown',
    orientation: null,
    capturedAtRaw: null,
    utcOffsetRaw: null,
    segments: [],
    trailingBytes: 0,
    error: `unsupported format: ${format}`,
  };
}

/** Sanitize by format. Throws on anything it cannot walk — FAIL CLOSED. */
export function sanitizePhoto(bytes: Uint8Array, format: PhotoFormat): Uint8Array {
  if (format === 'jpeg') return sanitizeJpeg(bytes);
  if (format === 'png') return sanitizePng(bytes);
  throw new Error(`unsupported format: ${format}`);
}

/** Verify by format. A non-empty array means NOT eligible. */
export function verifyPhotoClean(bytes: Uint8Array, format: PhotoFormat): string[] {
  if (format === 'jpeg') return verifyJpegClean(bytes);
  if (format === 'png') return verifyPngClean(bytes);
  return [`unsupported format: ${format}`];
}

/**
 * Did the picture itself survive untouched?
 *
 * Compares every non-metadata byte between source and output. Equal means the
 * archive is the same pixels, not a re-compression — which is what
 * "full-resolution source detail is retained" (PT §9) has to mean.
 */
export function pictureBytesPreserved(source: Uint8Array, sanitized: Uint8Array): boolean {
  const format = sniffFormat(source);
  const pick = (bytes: Uint8Array): Uint8Array[] => {
    if (format === 'jpeg') {
      const walk = walkJpeg(bytes);
      const kept: Uint8Array[] = [];
      let i = 2;
      while (i < walk.endOfImage - 2 && i < bytes.length) {
        let marker = u8(bytes, i + 1);
        while (marker === 0xff) {
          i++;
          marker = u8(bytes, i + 1);
        }
        if (marker === 0xd9) break;
        if (marker === 0xda) {
          const headerLength = (u8(bytes, i + 2) << 8) | u8(bytes, i + 3);
          const next = endOfScan(bytes, i + 2 + headerLength);
          kept.push(bytes.subarray(i, next));
          i = next;
          continue;
        }
        const length = (u8(bytes, i + 2) << 8) | u8(bytes, i + 3);
        if (!isJpegMetadataMarker(marker)) kept.push(bytes.subarray(i, i + 2 + length));
        i += 2 + length;
      }
      return kept;
    }
    const walk = walkPng(bytes);
    return walk.chunks
      .filter((chunk) => chunk.type === 'IDAT')
      .map((chunk) => bytes.subarray(chunk.at, chunk.at + 12 + chunk.length));
  };

  const a = concat(pick(source));
  const b = concat(pick(sanitized));
  return a.length === b.length && a.every((byte, index) => byte === b[index]);
}

/** 64 lowercase hex characters — the exact shape migration 0006's CHECK requires. */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}
